/**
 * Cart variant edit modal (ported from autel-dealer, adapted for classic cart page theme).
 */
class CartEdit {
  constructor() {
    this.modal = document.getElementById('Cart-Edit-Modal');
    if (!this.modal) return;

    this.content = this.modal.querySelector('[data-cart-edit-content]');
    this.lineKey = null;
    this.lineIndex = null;
    this.quantity = 1;
    this.variants = [];
    this.selectedOptions = [];

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
        this.close();
      }
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-edit]');
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-edit-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  open(trigger) {
    if (trigger.classList.contains('loading')) return;

    const url = trigger.dataset.url;
    this.lineKey = trigger.dataset.key;
    this.lineIndex = trigger.dataset.line;
    this.quantity = parseInt(trigger.dataset.quantity, 10) || 1;

    if (!url) return;

    trigger.classList.add('loading');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const template = doc.querySelector('[data-cart-edit-template]');
        if (!template) throw new Error('Cart edit template missing');

        this.content.innerHTML = template.outerHTML;
        this.bindTemplate();
        this.modal.removeAttribute('hidden');
        document.body.classList.add('cart-edit-open');
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        trigger.classList.remove('loading');
      });
  }

  close() {
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('cart-edit-open');
    this.content.innerHTML = '';
  }

  bindTemplate() {
    const root = this.content.querySelector('[data-cart-edit-template]');
    const form = root.querySelector('[data-cart-edit-form]');
    const qtyInput = root.querySelector('[data-cart-edit-quantity]');
    const variantsScript = root.querySelector('[data-cart-edit-variants]');

    if (qtyInput) qtyInput.value = this.quantity;

    root.querySelector('[data-cart-edit-qty-minus]')?.addEventListener('click', () => {
      const current = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = Math.max(1, current - 1);
    });

    root.querySelector('[data-cart-edit-qty-plus]')?.addEventListener('click', () => {
      const current = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = current + 1;
    });

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.selectedOptions = [];
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const checked = swatch.querySelector('input:checked');
      this.selectedOptions[parseInt(swatch.dataset.optionIndex, 10)] = checked ? checked.value : null;
    });

    root.querySelectorAll('[data-cart-edit-option]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = parseInt(input.dataset.optionPosition, 10);
        this.selectedOptions[position] = input.value;
        const header = input.closest('.cart-edit__swatch').querySelector('[data-header-option]');
        if (header) header.textContent = input.value;
        this.updateVariantUI(root);
      });
    });

    this.updateVariantUI(root);
    form.addEventListener('submit', (event) => this.onSubmit(event, root));
  }

  findVariant() {
    return this.variants.find((variant) => {
      return this.selectedOptions.every((value, index) => {
        if (value == null) return true;
        return variant[`option${index + 1}`] === value;
      });
    });
  }

  getVariantStrings() {
    return window.variantStrings || window.theme?.strings || {};
  }

  formatMoney(value) {
    if (value == null || value === '') return '';
    const str = String(value);
    if (str.includes('<')) {
      const tmp = document.createElement('div');
      tmp.innerHTML = str;
      return tmp.textContent || tmp.innerText || '';
    }
    return str;
  }

  getRoutes() {
    return window.routes || {
      cart_url: '/cart',
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change'
    };
  }

  updateVariantUI(root) {
    const variant = this.findVariant();
    const variantInput = root.querySelector('[data-cart-edit-variant-id]');
    const priceEl = root.querySelector('[data-cart-edit-price]');
    const compareEl = root.querySelector('[data-cart-edit-compare]');
    const labelEl = root.querySelector('[data-cart-edit-option-label]');
    const imageEl = root.querySelector('[data-cart-edit-image]');
    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    const strings = this.getVariantStrings();

    this.disableUnavailableOptions(root);

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        const label = submitBtn.querySelector('[data-cart-edit-submit-label]');
        if (label) label.textContent = strings.unavailable || 'Unavailable';
      }
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = this.formatMoney(variant.price_formatted);
    if (labelEl) labelEl.textContent = variant.title;

    if (compareEl) {
      const showCompare =
        variant.compare_at_price &&
        variant.price &&
        variant.compare_at_price > variant.price &&
        variant.compare_at_price_formatted;

      if (showCompare) {
        compareEl.textContent = this.formatMoney(variant.compare_at_price_formatted);
        compareEl.classList.remove('hide');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hide');
      }
    }

    if (imageEl && variant.featured_image) {
      imageEl.src = variant.featured_image;
    }

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      const label = submitBtn.querySelector('[data-cart-edit-submit-label]');
      if (label) {
        label.textContent = variant.available
          ? strings.addToCart || 'Add to cart'
          : strings.soldOut || strings.outOfStock || strings.unavailable || 'Sold out';
      }
    }
  }

  disableUnavailableOptions(root) {
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      swatch.querySelectorAll('.cart-edit__swatch-element').forEach((element) => {
        const value = element.dataset.value;
        const available = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return this.selectedOptions.every((selected, index) => {
            if (index === optionIndex || selected == null) return true;
            return variant[`option${index + 1}`] === selected;
          });
        });

        element.classList.toggle('soldout', !available);
        const input = element.querySelector('input');
        if (input) input.disabled = !available;
      });
    });
  }

  refreshCartPage() {
    const routes = this.getRoutes();
    window.location.href = routes.cart_url || '/cart';
  }

  onSubmit(event, root) {
    event.preventDefault();

    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    if (!submitBtn || submitBtn.disabled || submitBtn.classList.contains('loading')) return;

    const variant = this.findVariant();
    const quantity = parseInt(root.querySelector('[data-cart-edit-quantity]').value, 10) || 1;
    if (!variant || !variant.available) return;

    submitBtn.classList.add('loading');
    submitBtn.setAttribute('aria-disabled', 'true');

    const spinner = submitBtn.querySelector('.button-overlay-spinner');
    if (spinner) spinner.classList.remove('hidden');

    const routes = this.getRoutes();

    const removeBody = JSON.stringify({
      line: parseInt(this.lineIndex, 10),
      quantity: 0
    });

    fetch(routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: removeBody
    })
      .then((response) => response.json())
      .then(() => {
        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', String(quantity));

        return fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });
      })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || 'Unable to update cart');
        }

        this.close();
        this.refreshCartPage();
      })
      .catch((error) => {
        console.error(error);
        this.close();
        this.refreshCartPage();
      })
      .finally(() => {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      });
  }
}

window.addEventListener('load', () => {
  new CartEdit();
});
